import type { ToastMessage, ToastType } from '@/types/chat';
import { createId } from '@/utils/ids';

interface HelpAgentToastManagerOptions {
  durationMs?: number;
  onChange?: (toasts: ToastMessage[]) => void;
}

export class HelpAgentToastManager {
  private readonly durationMs: number;
  private readonly onChange?: (toasts: ToastMessage[]) => void;

  private toasts: ToastMessage[] = [];
  private readonly timers = new Map<string, number>();

  constructor(options: HelpAgentToastManagerOptions = {}) {
    this.durationMs = options.durationMs ?? 3200;
    this.onChange = options.onChange;
  }

  get snapshot(): ToastMessage[] {
    return [...this.toasts];
  }

  push(message: string, type: ToastType): ToastMessage {
    const toast: ToastMessage = {
      id: createId('toast'),
      message,
      type,
    };

    this.toasts = [...this.toasts, toast];
    this.emitChange();

    const timerId = window.setTimeout(() => {
      this.timers.delete(toast.id);
      this.remove(toast.id);
    }, this.durationMs);

    this.timers.set(toast.id, timerId);
    return toast;
  }

  clear(): void {
    for (const timerId of this.timers.values()) {
      window.clearTimeout(timerId);
    }

    this.timers.clear();
    this.toasts = [];
    this.emitChange();
  }

  destroy(): void {
    this.clear();
  }

  private remove(id: string): void {
    this.toasts = this.toasts.filter((toast) => toast.id !== id);
    this.emitChange();
  }

  private emitChange(): void {
    this.onChange?.([...this.toasts]);
  }
}
